import React, { useState, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Tooltip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Lazy load del SupportDrawer (solo se carga cuando el usuario hace clic)
const SupportDrawer = React.lazy(() => import('../../sections/help-and-support/SupportDrawer'));

const WELCOME_SHOWN_KEY = 'luky-welcome-shown';
const INACTIVITY_SHOWN_KEY = 'luky-inactivity-shown';
const INACTIVITY_TIMEOUT = 15000; // 15 segundos
const TOOLTIP_DURATION = 5000; // 5 segundos

export const FloatingSupportButton: React.FC = () => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showWelcomeTooltip, setShowWelcomeTooltip] = useState(false);
  const [showInactivityTooltip, setShowInactivityTooltip] = useState(false);
  const [shouldBlink, setShouldBlink] = useState(false);
  const [shouldGlowAntenna, setShouldGlowAntenna] = useState(false);

  // Mostrar tooltip de bienvenida solo una vez
  useEffect(() => {
    const hasShown = localStorage.getItem(WELCOME_SHOWN_KEY);
    if (!hasShown) {
      setShowWelcomeTooltip(true);
      localStorage.setItem(WELCOME_SHOWN_KEY, 'true');

      const timer = setTimeout(() => {
        setShowWelcomeTooltip(false);
      }, TOOLTIP_DURATION);

      return () => clearTimeout(timer);
    }
  }, []);

  // Cerrar tooltip de bienvenida al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      if (showWelcomeTooltip) {
        setShowWelcomeTooltip(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showWelcomeTooltip]);

  // Timer de inactividad con animación de antena
  useEffect(() => {
    const hasShownInactivity = sessionStorage.getItem(INACTIVITY_SHOWN_KEY);
    
    if (hasShownInactivity || isDrawerOpen) {
      return;
    }

    const timer = setTimeout(() => {
      // Animar la antena 3 veces antes de mostrar el tooltip
      let glowCount = 0;
      const glowInterval = setInterval(() => {
        setShouldGlowAntenna(true);
        setTimeout(() => setShouldGlowAntenna(false), 400);
        glowCount++;
        if (glowCount >= 3) {
          clearInterval(glowInterval);
          // Mostrar tooltip después de las 3 animaciones
          setTimeout(() => {
            setShowInactivityTooltip(true);
            sessionStorage.setItem(INACTIVITY_SHOWN_KEY, 'true');
            setTimeout(() => {
              setShowInactivityTooltip(false);
            }, TOOLTIP_DURATION);
          }, 200);
        }
      }, 600); // 600ms entre cada parpadeo de antena
    }, INACTIVITY_TIMEOUT);

    return () => clearTimeout(timer);
  }, [isDrawerOpen]);

  // Parpadeo de ojos aleatorio
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setShouldBlink(true);
      setTimeout(() => setShouldBlink(false), 300);
    }, Math.random() * 2000 + 4000); // Entre 4-6 segundos

    return () => clearInterval(blinkInterval);
  }, []);

  const handleClick = () => {
    setIsDrawerOpen(true);
    setShowWelcomeTooltip(false);
    setShowInactivityTooltip(false);
  };

  return (
    <>
      <Tooltip
        title={
          showWelcomeTooltip
            ? t('fab.welcomeTooltip', { ns: 'help-and-support' })
            : showInactivityTooltip
            ? t('fab.inactivityTooltip', { ns: 'help-and-support' })
            : ''
        }
        open={showWelcomeTooltip || showInactivityTooltip}
        placement="left"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#1E252D',
              fontSize: '14px',
              maxWidth: '280px',
              padding: '12px 16px',
              '& .MuiTooltip-arrow': {
                color: '#1E252D',
              },
            },
          },
        }}
      >
        <Box
          component={motion.div}
          sx={{
            position: 'fixed',
            bottom: { xs: '24px', md: '64px' },
            right: '24px',
            zIndex: 9999,
          }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <motion.button
            onClick={handleClick}
            style={{
              width: '80px',
              height: '80px',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              background: 'none',
              overflow: 'visible',
              position: 'relative',
            }}
            aria-label="Abrir asistente"
          >
            <svg width="80" height="80" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="glow-fab-green">
                  <feGaussianBlur stdDeviation="12" result="coloredBlur"/>
                  <feFlood floodColor="#39ff14" floodOpacity="1" result="glowColor"/>
                  <feComposite in="glowColor" in2="coloredBlur" operator="in" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <linearGradient id="linearGradient1-fab" x1="149.41" y1="62.5686" x2="149.41" y2="286.156" gradientUnits="userSpaceOnUse">
                  <stop offset="1e-05" stopColor="#59a9f9" stopOpacity="1"/>
                  <stop offset="1" stopColor="#0e6ecf" stopOpacity="1"/>
                </linearGradient>
                <linearGradient id="linearGradient2-fab" x1="148.884" y1="216.303" x2="148.884" y2="289.911" gradientUnits="userSpaceOnUse">
                  <stop offset="0.152089" stopColor="#f3f9ff" stopOpacity="1"/>
                  <stop offset="0.906176" stopColor="#d9ecff" stopOpacity="1"/>
                </linearGradient>
                <linearGradient id="linearGradient3-fab" x1="57.659" y1="106.935" x2="57.659" y2="148.665" gradientUnits="userSpaceOnUse">
                  <stop offset="1e-05" stopColor="#f3f9ff" stopOpacity="1"/>
                  <stop offset="1" stopColor="#d9ecff" stopOpacity="1"/>
                </linearGradient>
                <linearGradient id="linearGradient4-fab" x1="242.933" y1="106.935" x2="242.933" y2="148.665" gradientUnits="userSpaceOnUse">
                  <stop offset="1e-05" stopColor="#f3f9ff" stopOpacity="1"/>
                  <stop offset="1" stopColor="#d9ecff" stopOpacity="1"/>
                </linearGradient>
                <linearGradient id="linearGradient5-fab" x1="149.595" y1="51.1021" x2="149.595" y2="204.866" gradientUnits="userSpaceOnUse">
                  <stop offset="0.474101" stopColor="#f3f9ff" stopOpacity="1"/>
                  <stop offset="1" stopColor="#d9ecff" stopOpacity="1"/>
                </linearGradient>
                <linearGradient id="linearGradient6-fab" x1="149.597" y1="79.0588" x2="149.597" y2="176.909" gradientUnits="userSpaceOnUse">
                  <stop offset="1e-05" stopColor="#e1f0ff" stopOpacity="1"/>
                  <stop offset="0.520358" stopColor="#b0d8ff" stopOpacity="1"/>
                  <stop offset="1" stopColor="#8fc1f4" stopOpacity="1"/>
                </linearGradient>
              </defs>
              
              {/* Antena con animación verde */}
              <g className="default-antenna-fab">
                <path 
                  fill={shouldGlowAntenna || showWelcomeTooltip || showInactivityTooltip ? "#00ff00" : "#c3e1ff"}
                  stroke="#071451" 
                  strokeWidth="7.6027" 
                  d="M 148.522003 10.446991 C 156.039993 10.446991 162.192993 16.495911 162.192993 24.029114 C 162.192993 31.562408 156.039993 37.611084 148.522003 37.611084 C 141.005005 37.610992 134.852005 31.562408 134.852005 24.029114 C 134.852005 16.495911 141.005005 10.447113 148.522003 10.446991 Z"
                  style={{
                    transition: 'fill 0.3s ease'
                  }}
                />
                <path fill="none" stroke="#071451" strokeWidth="7.6027" d="M 148.951004 54.22229 L 148.951004 37.212402"/>
              </g>
              
              {/* Cuerpo */}
              <path fill="url(#linearGradient1-fab)" stroke="none" d="M 262.214996 174.362 C 262.214996 236.103577 211.71048 286.154999 149.410004 286.154999 C 87.10952 286.154999 36.605003 236.103577 36.605003 174.362 C 36.605003 112.620422 87.10952 62.569 149.410004 62.569 C 211.71048 62.569 262.214996 112.620422 262.214996 174.362 Z"/>
              
              {/* Ropa */}
              <path fill="url(#linearGradient2-fab)" stroke="none" d="M 91.25 232.822998 C 80.991997 239.940002 79.709999 259.51001 80.350998 268.404999 C 79.281998 269.463989 86.377998 274.505005 123.307007 286.196014 C 160.237 297.886993 201.526001 279.207001 217.554993 268.404999 L 217.554993 262.050995 L 214.990005 244.895996 L 200.884995 227.740005 L 189.985992 221.386002 L 172.033997 218.209 C 163.057999 217.574005 141.516006 216.302994 127.154007 216.302994 C 109.201996 216.302994 104.072998 223.927994 91.25 232.822998 Z"/>
              
              {/* Contorno */}
              <path fill="none" stroke="#071451" strokeWidth="7.6027" d="M 149.410004 58.766785 C 213.776993 58.766785 266.016998 110.488007 266.016998 174.362 C 266.016998 238.235001 213.776993 289.957001 149.410004 289.957001 C 85.042 289.957001 32.8032 238.235001 32.8032 174.362 C 32.803501 110.488007 85.042 58.76709 149.410004 58.766785 Z"/>
              <path fill="none" stroke="#071451" strokeWidth="7.83247" d="M 218.199005 268.404999 C 218.199005 240.332001 195.440994 217.574005 167.367996 217.574005 L 131.826004 217.574005 C 103.753006 217.574005 80.995003 240.332001 80.995003 268.404999"/>
              
              {/* Orejas */}
              <path fill="url(#linearGradient3-fab)" stroke="none" d="M 78.712997 127.800003 C 78.712997 139.323532 69.286804 148.665207 57.658997 148.665207 C 46.031197 148.665207 36.605003 139.323532 36.605003 127.800003 C 36.605003 116.276459 46.031197 106.934799 57.658997 106.934799 C 69.286804 106.934799 78.712997 116.276459 78.712997 127.800003 Z"/>
              <path fill="url(#linearGradient4-fab)" stroke="none" d="M 263.987 127.800003 C 263.987 139.323532 254.560791 148.665207 242.933014 148.665207 C 231.305206 148.665207 221.878998 139.323532 221.878998 127.800003 C 221.878998 116.276459 231.305206 106.934799 242.933014 106.934799 C 254.560791 106.934799 263.987 116.276459 263.987 127.800003 Z"/>
              
              {/* Cabeza */}
              <path fill="url(#linearGradient5-fab)" stroke="#071451" strokeWidth="7.6027" d="M 132.656906 51.102112 L 166.532898 51.102112 C 208.521454 51.102112 242.559906 85.140564 242.559906 127.129105 L 242.559906 128.839096 C 242.559906 170.827652 208.521454 204.866104 166.532898 204.866104 L 132.656906 204.866104 C 90.66835 204.866104 56.629898 170.827652 56.629898 128.839096 L 56.629898 127.129105 C 56.629898 85.140564 90.66835 51.102112 132.656906 51.102112 Z"/>
              
              {/* Cara */}
              <path fill="url(#linearGradient6-fab)" stroke="#071451" strokeWidth="7.6027" d="M 124.653 79.058807 L 174.539993 79.058807 C 200.068253 79.058807 220.763 99.75354 220.763 125.281799 L 220.763 130.685699 C 220.763 156.213959 200.068253 176.908707 174.539993 176.908707 L 124.653 176.908707 C 99.124741 176.908707 78.43 156.213959 78.43 130.685699 L 78.43 125.281799 C 78.43 99.75354 99.124741 79.058807 124.653 79.058807 Z"/>
              
              {/* Boca */}
              <path fill="none" stroke="#071451" strokeWidth="7.6027" strokeLinecap="round" d="M 135.261993 133.408005 C 135.261993 133.408005 139.707993 140.619003 149.813995 140.619003 C 159.919006 140.619003 164.365997 133.408005 164.365997 133.408005"/>
              
              {/* Ojos con parpadeo */}
              <path className="default-eye-fab-1" fill="#071451" stroke="none" d="M 125.559158 120.98999 C 125.559158 126.078629 121.396683 130.203781 116.261993 130.203781 C 111.127319 130.203781 106.964844 126.078629 106.964844 120.98999 C 106.964844 115.901367 111.127319 111.776215 116.261993 111.776215 C 121.396683 111.776215 125.559158 115.901367 125.559158 120.98999 Z"/>
              
              {/* Contorno orejas */}
              <path fill="none" stroke="#071451" strokeWidth="7.6027" d="M 241.825989 150.386993 C 254.06601 150.386993 263.988007 140.167999 263.988007 127.563004 C 263.988007 114.957001 254.06601 104.738007 241.825989 104.738007"/>
              <path fill="none" stroke="#071451" strokeWidth="7.6027" d="M 56.993202 150.386993 C 44.753403 150.386993 34.8311 140.167999 34.8311 127.563004 C 34.8311 114.957001 44.753403 104.738007 56.993202 104.738007"/>
              
              <path className="default-eye-fab-2" fill="#071451" stroke="none" d="M 191.853165 120.98999 C 191.853165 126.078629 187.690674 130.203781 182.556 130.203781 C 177.421326 130.203781 173.258835 126.078629 173.258835 120.98999 C 173.258835 115.901367 177.421326 111.776215 182.556 111.776215 C 187.690674 111.776215 191.853165 115.901367 191.853165 120.98999 Z"/>
              
              <style>{`
                .default-eye-fab-1 {
                  transform: ${shouldBlink ? 'scaleY(0.1)' : 'scaleY(1)'};
                  transform-origin: 116.262px 120.99px;
                  transition: transform 0.15s ease;
                }
                .default-eye-fab-2 {
                  transform: ${shouldBlink ? 'scaleY(0.1)' : 'scaleY(1)'};
                  transform-origin: 182.556px 120.99px;
                  transition: transform 0.15s ease;
                }
              `}</style>
            </svg>
          </motion.button>
        </Box>
      </Tooltip>

      <Suspense fallback={null}>
        <SupportDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
      </Suspense>
    </>
  );
};

export default FloatingSupportButton;
